import React, { useState } from "react";
import {
  Link,
  useLocation,
  useParams,
  useSearchParams,
} from "react-router-dom";
import Breadcrumb from "../components/ui/Breadcrumb";
import EmptyState from "../components/ui/EmptyState";
import ErrorState from "../components/ui/ErrorState";
import PageHeader from "../components/ui/PageHeader";
import Pagination from "../components/ui/Pagination";
import RefreshButton from "../components/ui/RefreshButton";
import TableSkeleton from "../components/ui/TableSkeleton";
import KpiStrip from "../components/inconsistencies/KpiStrip";
import OccurrenceList from "../components/inconsistencies/OccurrenceList";
import RuleDrawer from "../components/inconsistencies/RuleDrawer";
import RuleGroupFilter from "../components/inconsistencies/RuleGroupFilter";
import RuleTable from "../components/inconsistencies/RuleTable";
import SprintChips from "../components/inconsistencies/SprintChips";
import SprintGroupHeader from "../components/inconsistencies/SprintGroupHeader";
import {
  ALL_GROUPS,
  filterByRule,
  filterRowsByGroup,
  sortRulesByCount,
  summarize,
} from "../domain/inconsistencies";
import { ruleById } from "../domain/inconsistencyRules";
import { useInconsistencies } from "../hooks/useInconsistencies";
import { useSprints } from "../hooks/useSprints";
import { DEFAULT_PAGE_SIZE, totalPagesOf } from "../lib/pagination";
import { resolveProjectName } from "../lib/projectNameCache";
import { paths } from "../routes/paths";
import "./Inconsistencies.css";

const SKELETON_COLUMNS = [
  { key: "rule", label: "Regra", bars: 2 },
  { key: "group", label: "Grupo", className: "col-group" },
  { key: "occ", label: "Ocorrências", className: "col-occ" },
  { key: "status", label: "Status", className: "col-status" },
  { key: "go", label: "", className: "col-go", bars: 0 },
];

function parseSprintIds(value) {
  return (value || "")
    .split(",")
    .map((id) => id.trim())
    .filter(Boolean);
}

// Página corrente sempre dentro do intervalo: recarregar ou filtrar pode
// encolher a lista abaixo da página em que a tela estava.
function pageSlice(list, page, pageSize) {
  const totalPages = totalPagesOf(list.length, pageSize);
  const current = Math.min(page, Math.max(totalPages, 1));
  return {
    totalPages,
    current,
    visible: list.slice((current - 1) * pageSize, current * pageSize),
  };
}

function occurrencesHint(count) {
  return count === 1 ? "1 no total" : `${count} no total`;
}

function joinSprintNames(sprints) {
  const names = sprints.map((sprint) => sprint.name);
  if (names.length === 0) return null;
  if (names.length === 1) return names[0];
  return `${names.slice(0, -1).join(", ")} e ${names[names.length - 1]}`;
}

function auditedScope(sprints) {
  const names = joinSprintNames(sprints);
  return names && `Nada a corrigir em ${names}.`;
}

function Inconsistencies() {
  const { projectId } = useParams();
  const location = useLocation();
  const [searchParams, setSearchParams] = useSearchParams();
  const [openRuleId, setOpenRuleId] = useState(null);
  const [groupFilter, setGroupFilter] = useState(ALL_GROUPS);
  const [rulePage, setRulePage] = useState(1);
  const [rulePageSize, setRulePageSize] = useState(DEFAULT_PAGE_SIZE);

  const requestedIds = parseSprintIds(searchParams.get("sprints"));
  const {
    sprints,
    loading: loadingSprints,
    error: sprintsError,
  } = useSprints(projectId);

  // A mesma requisição que resolve os nomes valida os ids: sprint que não
  // pertence ao projeto é descartada em vez de virar chip fantasma.
  const auditedSprints = requestedIds
    .map((id) => sprints.find((sprint) => String(sprint.id) === id))
    .filter(Boolean);
  const auditedIds = auditedSprints.map((sprint) => String(sprint.id));

  const { items, groups, projectLevelItems, loading, error, reload } =
    useInconsistencies({
      projectId,
      sprintIds: auditedIds,
    });

  const projectName = resolveProjectName(projectId, location.state, sprints);
  const summary = summarize(items);

  const busy = loadingSprints || loading;
  const failed = sprintsError || error;
  const nothingSelected =
    requestedIds.length === 0 ||
    (!loadingSprints && auditedSprints.length === 0);

  // O recorte por regra alimenta só o painel: a lista da página mostra a
  // apuração inteira, porque foi o clique que a filtrava que saiu daqui.
  const groupsOfRule = (ruleId) =>
    groups
      .map((group) => ({
        key: `sprint-${group.sprintId}`,
        title: auditedSprints.find(
          (sprint) => String(sprint.id) === group.sprintId
        )?.name,
        items: filterByRule(group.items, ruleId),
      }))
      .concat({
        key: "project",
        title: "Projeto",
        items: filterByRule(projectLevelItems, ruleId),
      })
      .filter((group) => group.items.length > 0);

  const sprintGroups = groupsOfRule(null);
  const openRule = ruleById(openRuleId);

  const ruleRows = filterRowsByGroup(
    sortRulesByCount(summary.byRule),
    groupFilter
  );
  const rules = pageSlice(ruleRows, rulePage, rulePageSize);

  const occurrenceCount = sprintGroups.reduce(
    (total, group) => total + group.items.length,
    0
  );

  const changeGroupFilter = (group) => {
    setGroupFilter(group);
    setRulePage(1);
  };

  const removeSprint = (sprintId) => {
    const remaining = auditedIds.filter((id) => id !== sprintId);
    setSearchParams(
      remaining.length > 0 ? { sprints: remaining.join(",") } : {}
    );
    setRulePage(1);
  };

  const trail = (
    <Breadcrumb
      items={[
        { label: projectName, to: paths.projects },
        { label: "Sprints", to: paths.sprints(projectId) },
        { label: "Inconsistências" },
      ]}
    />
  );

  if (nothingSelected) {
    return (
      <>
        {trail}
        <PageHeader title="Inconsistências" />
        <div className="card data-card">
          <EmptyState
            icon="folder"
            title="Selecione ao menos uma sprint"
            help="A apuração acontece sobre as sprints escolhidas na tela anterior."
            action={
              <Link className="btn btn-primary" to={paths.sprints(projectId)}>
                Escolher sprints
              </Link>
            }
          />
        </div>
      </>
    );
  }

  return (
    <>
      {trail}

      <PageHeader title="Inconsistências">
        <RefreshButton onClick={reload} loading={busy} />
      </PageHeader>

      <div className="filters">
        <SprintChips sprints={auditedSprints} onRemove={removeSprint} />
      </div>

      <section className="section">
        <div className="section-head has-sub">
          <h2 className="section-title">Indicadores</h2>
        </div>

        <KpiStrip summary={summary} loading={busy} />
      </section>

      <section className="section">
        <div className="section-head has-control">
          <h2 className="section-title">Detalhamento por Regra</h2>
          <RuleGroupFilter value={groupFilter} onChange={changeGroupFilter} />
        </div>

        <div className="card data-card">
          {busy && (
            <TableSkeleton
              columns={SKELETON_COLUMNS}
              label="Apurando inconsistências"
            />
          )}

          {!busy && failed && (
            <ErrorState
              title="Não foi possível apurar as inconsistências"
              message="Verifique sua conexão e tente novamente."
              onRetry={reload}
            />
          )}

          {!busy && !failed && (
            <RuleTable rows={rules.visible} onOpenRule={setOpenRuleId} />
          )}
        </div>

        {!busy && !failed && (
          <Pagination
            label="Paginação das regras"
            page={rules.current}
            totalPages={rules.totalPages}
            total={ruleRows.length}
            pageSize={rulePageSize}
            onPageChange={setRulePage}
            onPageSizeChange={setRulePageSize}
          />
        )}
      </section>

      {!busy && !failed && (
        <section className="section">
          <div className="section-head">
            <h2 className="section-title">Todas as Ocorrências</h2>
            <span className="section-hint">
              {occurrencesHint(occurrenceCount)}
            </span>
          </div>

          {occurrenceCount === 0 ? (
            <div className="card data-card">
              <OccurrenceList
                items={[]}
                cleanScope={auditedScope(auditedSprints)}
              />
            </div>
          ) : (
            sprintGroups.map((group) => (
              <section
                className="card data-card group-card"
                key={group.key}
                aria-labelledby={`${group.key}-title`}
              >
                <SprintGroupHeader
                  id={`${group.key}-title`}
                  title={group.title}
                  count={group.items.length}
                />
                <OccurrenceList items={group.items} />
              </section>
            ))
          )}
        </section>
      )}

      {openRule && (
        <RuleDrawer
          rule={openRule}
          groups={groupsOfRule(openRule.id)}
          onClose={() => setOpenRuleId(null)}
        />
      )}
    </>
  );
}

export default Inconsistencies;
